/**
 * Live status/sentSchedule and status/previousSentSchedule of a device (BA-84 nodes, BA-85)
 * Follows the pattern of useCurrentAction.ts. Only the backend writes these nodes.
 */

import { useEffect, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import { SentSchedule } from '../types/sentSchedule';

export interface SentScheduleState {
  sentSchedule: SentSchedule | null;
  previousSentSchedule: SentSchedule | null;
  /** Both listeners have delivered a first value for this device (n2) */
  loaded: boolean;
}

export function useSentSchedule(deviceId: string): SentScheduleState {
  const [sentSchedule, setSentSchedule] = useState<SentSchedule | null>(null);
  const [previousSentSchedule, setPreviousSentSchedule] = useState<SentSchedule | null>(null);
  const [sentLoaded, setSentLoaded] = useState(false);
  const [previousLoaded, setPreviousLoaded] = useState(false);

  useEffect(() => {
    // Another device: never show the previous device's node while this one loads
    setSentSchedule(null);
    setPreviousSentSchedule(null);
    setSentLoaded(false);
    setPreviousLoaded(false);
    if (!deviceId) {
      return;
    }
    const listen = (
      node: string,
      set: (value: SentSchedule | null) => void,
      setLoaded: (loaded: boolean) => void,
    ) => onValue(
      ref(database, `/devices/${deviceId}/status/${node}`),
      (snapshot) => {
        set(snapshot.exists() ? (snapshot.val() as SentSchedule) : null);
        setLoaded(true);
      },
      (err) => console.error(`Error listening to ${node}:`, err)
    );
    const offSent = listen('sentSchedule', setSentSchedule, setSentLoaded);
    const offPrevious = listen('previousSentSchedule', setPreviousSentSchedule, setPreviousLoaded);
    return () => {
      offSent();
      offPrevious();
    };
  }, [deviceId]);

  return { sentSchedule, previousSentSchedule, loaded: sentLoaded && previousLoaded };
}
